// Undo/redo history for the contenteditable editor.
//
// The browser's native undo stack is unreliable here: we mutate the DOM
// directly for auto-format, auto-link, emoticon replacement, list/quote/code
// commands, and `setMarkdown` — none of which the native undo model tracks, so
// Cmd/Ctrl+Z ends up doing nothing or jumping unpredictably. Instead we own a
// snapshot history: each meaningful change records the editor's HTML + a
// caret offset, and Cmd/Ctrl+Z / redo restore a snapshot exactly.
//
// Typing is COALESCED: rapid successive edits collapse into one undo step
// (back to the pre-burst state) so undo doesn't crawl character-by-character,
// matching native editors.

const CHIP_SELECTOR = '[data-ping-code-lang-chip]';

export interface EditorSnapshot {
  /** Editor HTML with injected chrome (code-lang chips) stripped. */
  readonly html: string;
  /** Caret position as a global text offset (chips excluded). */
  readonly caret: number;
}

/** Snapshot the editor: HTML sans chrome + the caret's global text offset. */
export function snapshotEditor(root: HTMLElement): EditorSnapshot {
  const clone = root.cloneNode(true) as HTMLElement;
  clone.querySelectorAll(CHIP_SELECTOR).forEach((n) => n.remove());
  return { html: clone.innerHTML, caret: caretOffset(root) };
}

/** Global text offset of the collapsed caret, skipping chrome subtrees. */
function caretOffset(root: HTMLElement): number {
  const sel = root.ownerDocument.defaultView?.getSelection() ?? null;
  if (sel === null || sel.rangeCount === 0) return -1;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer)) return -1;
  let offset = 0;
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        return isInChip(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      },
    },
  );
  let node = walker.nextNode();
  while (node !== null) {
    if (node === range.startContainer) return offset + range.startOffset;
    offset += (node.nodeValue ?? '').length;
    node = walker.nextNode();
  }
  return offset;
}

function isInChip(node: Node): boolean {
  let cur: Node | null = node;
  while (cur !== null) {
    if (
      cur.nodeType === Node.ELEMENT_NODE &&
      (cur as HTMLElement).hasAttribute?.('data-ping-code-lang-chip')
    ) {
      return true;
    }
    cur = cur.parentNode;
  }
  return false;
}

/** Restore the caret to a global text offset (chips excluded). */
export function restoreCaretOffset(root: HTMLElement, target: number): void {
  const doc = root.ownerDocument;
  const sel = doc.defaultView?.getSelection() ?? null;
  if (sel === null) return;
  if (target < 0) {
    // Unknown caret → end of editor.
    const range = doc.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
    return;
  }
  let remaining = target;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return isInChip(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
    },
  });
  let node = walker.nextNode();
  let last: Text | null = null;
  while (node !== null) {
    const len = (node.nodeValue ?? '').length;
    last = node as Text;
    if (remaining <= len) {
      const range = doc.createRange();
      range.setStart(node, remaining);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    remaining -= len;
    node = walker.nextNode();
  }
  // Past the end → place after the last text node (or at editor end).
  const range = doc.createRange();
  if (last !== null) {
    range.setStart(last, (last.nodeValue ?? '').length);
  } else {
    range.selectNodeContents(root);
  }
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Coalescing undo/redo stack. `record` is called after every change; edits
 * within `coalesceMs` of each other collapse into one step.
 */
export class EditorHistory {
  private past: EditorSnapshot[] = [];
  private future: EditorSnapshot[] = [];
  private current: EditorSnapshot | null = null;
  private lastRecordAt = 0;

  constructor(private readonly coalesceMs = 350) {}

  /** Seed / hard-reset (mount, external setMarkdown) — clears undo history. */
  reset(snap: EditorSnapshot): void {
    this.past = [];
    this.future = [];
    this.current = snap;
    this.lastRecordAt = 0;
  }

  /**
   * Record the latest state. `now` is a millisecond timestamp (passed in so the
   * module stays deterministic). `force` starts a fresh undo step regardless of
   * timing (used for structural edits like paste).
   */
  record(snap: EditorSnapshot, now: number, force = false): void {
    if (this.current === null) {
      this.current = snap;
      this.lastRecordAt = now;
      return;
    }
    if (this.current.html === snap.html) {
      // Caret-only movement — keep the html step, just track the latest caret.
      this.current = snap;
      return;
    }
    const withinWindow = now - this.lastRecordAt < this.coalesceMs;
    if (force || !withinWindow) {
      // Begin a new undo step: commit the pre-change state.
      this.past.push(this.current);
      this.future = [];
    }
    // Within the window we extend the current burst (pre-burst state already
    // pushed), so just advance `current`.
    this.current = snap;
    this.lastRecordAt = now;
  }

  canUndo(): boolean {
    return this.past.length > 0;
  }
  canRedo(): boolean {
    return this.future.length > 0;
  }

  undo(): EditorSnapshot | null {
    if (this.past.length === 0 || this.current === null) return null;
    this.future.push(this.current);
    const prev = this.past.pop()!;
    this.current = prev;
    return prev;
  }

  redo(): EditorSnapshot | null {
    if (this.future.length === 0 || this.current === null) return null;
    this.past.push(this.current);
    const next = this.future.pop()!;
    this.current = next;
    return next;
  }
}
